import React from 'react';
import { SubHeaderGlobalKanbanControls } from './SubHeaderGlobalKanbanControls';
import { SubHeaderTaskViewControls } from './SubHeaderTaskViewControls';
import { SubHeaderAnalysisControls } from './SubHeaderAnalysisControls';
import { SubHeaderVisibilityDropdown } from '../dropdowns/SubHeaderVisibilityDropdown';
import { FullProject } from '../../../types';

export interface SubHeaderMiddleTabsProps {
  viewMode: string;
  kanbanMode: 'status' | 'date' | 'custom';
  setKanbanMode: (mode: 'status' | 'date' | 'custom') => void;
  communicationTab: 'board' | 'database' | 'network' | 'archive';
  setCommunicationTab: (tab: 'board' | 'database' | 'network' | 'archive') => void;
  activeAnalysisTab: string;
  setActiveAnalysisTab: (tab: any) => void;
  selectedAnalysisProjectId: string;
  setSelectedAnalysisProjectId: (id: string) => void;
  dropdownProjects: FullProject[];
  activeMetricsTab?: 'overview' | 'roadmap' | 'time' | 'projects' | 'library' | 'ideaPool' | 'annualReport' | 'activity';
  setActiveMetricsTab?: (tab: 'overview' | 'roadmap' | 'time' | 'projects' | 'library' | 'ideaPool' | 'annualReport' | 'activity') => void;
  activePoolTab?: 'activeProjects' | 'archive' | 'library' | 'ideaPool';
  setActivePoolTab?: (tab: 'activeProjects' | 'archive' | 'library' | 'ideaPool') => void;
  activeAITab?: 'inbox' | 'planning' | 'protocol';
  setActiveAITab?: (tab: 'inbox' | 'planning' | 'protocol') => void;
  isStandardTaskView: boolean;
  uiMode?: 'standard' | 'expert';
  onToggleUiMode?: () => void;
  setViewMode: (mode: any) => void;
  onPrefetch?: (view: string) => void;
  activeCalendarTab: 'calendar' | 'milestones';
  setActiveCalendarTab: (tab: 'calendar' | 'milestones') => void;
  
  // Global Kanban Props
  globalFilterAssignee: string;
  setGlobalFilterAssignee: (val: string) => void;
  globalFilterStatus: string;
  setGlobalFilterStatus: (val: string) => void;
  globalFilterType: string;
  setGlobalFilterType: (val: string) => void;
  globalAllAssignees: string[];
  globalAvailableTypes: string[];
  globalAvailableStatuses: string[];
  workspaceProjects?: FullProject[];
  hiddenProjectIds?: string[];
  setHiddenProjectIds?: (ids: string[]) => void;
  showCompleted?: boolean;
  setShowCompleted?: (val: boolean) => void;
  showArchived?: boolean;
  setShowArchived?: (val: boolean) => void;
  showTaskDetails?: boolean;
  setShowTaskDetails?: (val: boolean) => void;
}

export const SubHeaderMiddleTabs: React.FC<SubHeaderMiddleTabsProps> = ({
  viewMode,
  kanbanMode,
  setKanbanMode,
  communicationTab,
  setCommunicationTab,
  activeAnalysisTab,
  setActiveAnalysisTab,
  selectedAnalysisProjectId,
  setSelectedAnalysisProjectId,
  dropdownProjects,
  activeMetricsTab,
  setActiveMetricsTab,
  activePoolTab,
  setActivePoolTab,
  activeAITab,
  setActiveAITab,
  isStandardTaskView,
  uiMode,
  onToggleUiMode,
  setViewMode,
  onPrefetch,
  activeCalendarTab,
  setActiveCalendarTab,
  globalFilterAssignee,
  setGlobalFilterAssignee,
  globalFilterStatus,
  setGlobalFilterStatus,
  globalFilterType,
  setGlobalFilterType,
  globalAllAssignees,
  globalAvailableTypes,
  globalAvailableStatuses,
  workspaceProjects = [],
  hiddenProjectIds = [],
  setHiddenProjectIds = () => {},
  showCompleted = true,
  setShowCompleted,
  showArchived = false,
  setShowArchived,
  showTaskDetails = true,
  setShowTaskDetails = () => {}
}) => {
  return (
    <div className="flex-1 flex justify-center items-center gap-4 px-4 h-full">
      {/* Global Kanban Mode Switcher & Filters */}
      {viewMode === 'globalKanban' && (
        <SubHeaderGlobalKanbanControls
          kanbanMode={kanbanMode}
          setKanbanMode={setKanbanMode}
          globalFilterAssignee={globalFilterAssignee}
          setGlobalFilterAssignee={setGlobalFilterAssignee}
          globalFilterStatus={globalFilterStatus}
          setGlobalFilterStatus={setGlobalFilterStatus}
          globalFilterType={globalFilterType}
          setGlobalFilterType={setGlobalFilterType}
          allAssignees={globalAllAssignees}
          availableTypes={globalAvailableTypes}
          availableStatuses={globalAvailableStatuses}
          workspaceProjects={workspaceProjects}
          hiddenProjectIds={hiddenProjectIds}
          setHiddenProjectIds={setHiddenProjectIds}
          showCompleted={showCompleted}
          setShowCompleted={setShowCompleted}
          showArchived={showArchived}
          setShowArchived={setShowArchived}
          showTaskDetails={showTaskDetails}
          setShowTaskDetails={setShowTaskDetails}
        />
      )}

      {/* Communication Section Switcher */}
      {viewMode === 'communication' && !isStandardTaskView && (
        <div className="flex items-center gap-4">
          <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200/50 dark:border-slate-800 h-[34px] items-center shadow-inner gap-0.5">
            <button 
              type="button"
              onClick={() => setCommunicationTab('board')} 
              translate="no"
              className={`px-4 h-full rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${communicationTab === 'board' ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-500'}`}
            >
              Kanban
            </button>
            <button 
              type="button"
              onClick={() => setCommunicationTab('database')} 
              className={`px-4 h-full rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${communicationTab === 'database' ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-500'}`}
            >
              Verzeichnis
            </button>
            <button 
              type="button"
              onClick={() => setCommunicationTab('network')} 
              className={`px-4 h-full rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${communicationTab === 'network' ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-500'}`}
            >
              Netzwerk
            </button>
          </div>

          {communicationTab === 'board' && setShowArchived && (
            <>
              <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-1" />
              <SubHeaderVisibilityDropdown
                showCompleted={showCompleted}
                setShowCompleted={setShowCompleted}
                showArchived={showArchived}
                setShowArchived={setShowArchived}
                showTaskDetails={showTaskDetails}
                setShowTaskDetails={setShowTaskDetails}
              />
            </>
          )}
        </div>
      )}

      {/* Metrics Dashboard Tabs */}
      {viewMode === 'metricsDashboard' && activeMetricsTab && setActiveMetricsTab && (
        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200/50 dark:border-slate-800 h-[34px] items-center shadow-inner gap-0.5">
          {(['overview', 'projects', 'roadmap', 'time', 'annualReport', 'activity'] as const).map((tab) => {
            const labels: Record<string, string> = {
              overview: 'Aufgaben',
              projects: 'Projekte',
              roadmap: 'Roadmap',
              time: 'Zeiterfassung',
              annualReport: 'Tätigkeitsbericht',
              activity: 'Aktivitäten'
            };
            return (
              <button 
                key={tab}
                type="button"
                onClick={() => setActiveMetricsTab(tab)} 
                className={`px-6 h-full rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${
                  activeMetricsTab === tab 
                    ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600' 
                    : 'text-slate-400 hover:text-slate-500'
                }`}
              >
                {labels[tab]}
              </button>
            );
          })}
        </div>
      )}

      {/* Projektpool Tabs */}
      {viewMode === 'projectPool' && activePoolTab && setActivePoolTab && (
        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200/50 dark:border-slate-800 h-[34px] items-center shadow-inner gap-0.5">
          {[
            { id: 'activeProjects', label: 'Aktiv' },
            { id: 'archive', label: 'Archiv' },
            { id: 'library', label: 'Vorlagen' },
            { id: 'ideaPool', label: 'Ideenpool' }
          ].map(t => (
            <button 
              key={t.id}
              type="button"
              onClick={() => setActivePoolTab(t.id as any)} 
              className={`px-5 h-7 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all duration-200 flex items-center justify-center ${
                activePoolTab === t.id 
                  ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600' 
                  : 'text-slate-400 hover:text-slate-500'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* AI Assistant Tabs */}
      {viewMode === 'aiAssistant' && activeAITab && setActiveAITab && (
        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200/50 dark:border-slate-800 h-[34px] items-center shadow-inner gap-0.5">
          {[
            { id: 'inbox', label: 'Notizbuch' },
            { id: 'planning', label: 'Projektschmiede' },
            { id: 'protocol', label: 'Protokollant' }
          ].map(t => (
            <button 
              key={t.id}
              type="button"
              onClick={() => setActiveAITab(t.id as any)} 
              className={`px-4 h-full rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${
                activeAITab === t.id 
                  ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600 dark:text-indigo-400' 
                  : 'text-slate-400 hover:text-slate-500'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Analysis Selectors & methodology tabs */}
      {viewMode === 'analysis' && (
        <SubHeaderAnalysisControls
          viewMode={viewMode}
          activeAnalysisTab={activeAnalysisTab}
          setActiveAnalysisTab={setActiveAnalysisTab}
          selectedAnalysisProjectId={selectedAnalysisProjectId}
          setSelectedAnalysisProjectId={setSelectedAnalysisProjectId}
          dropdownProjects={dropdownProjects}
          setViewMode={setViewMode}
          onPrefetch={onPrefetch}
        />
      )}

      {/* Standard Task View (Kanban, Gantt, List, Calendar, etc.) */}
      {isStandardTaskView && (
        <SubHeaderTaskViewControls
          viewMode={viewMode}
          setViewMode={setViewMode}
          kanbanMode={kanbanMode}
          setKanbanMode={setKanbanMode}
          uiMode={uiMode}
          onToggleUiMode={onToggleUiMode}
          onPrefetch={onPrefetch}
        />
      )}

      {/* Global Calendar Tabs (Dashboard) */}
      {viewMode === 'dashboard' && (
        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200/50 dark:border-slate-800 h-[34px] items-center shadow-inner gap-0.5">
          {[
            { id: 'calendar', label: 'Kalender' },
            { id: 'milestones', label: 'Meilensteine' }
          ].map((tab) => (
            <button 
              key={tab.id}
              type="button"
              onClick={() => setActiveCalendarTab(tab.id as any)} 
              className={`flex items-center gap-2 px-6 h-full rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${
                activeCalendarTab === tab.id 
                  ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600' 
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
